import { useEffect, useState } from 'react';
import Header from '../.shared/header/header';
import Footer from '../.shared/footer/footer';
import styles from './PaymentSuccess.module.css';
import { useLocation } from 'react-router-dom';

function PaymentSuccess() {
    const [telegraUrl, setTelegraUrl] = useState('');
    const [plan, setPlan] = useState(null);
    const [countdown, setCountdown] = useState(20);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { state } = useLocation();
    const patientPortalURL = process.env.REACT_APP_PATIENT_PORTAL_URL || 'https://patient.tb2.health';

    useEffect(() => {
        if (state?.patientPortalRegistrationToken) {
            // If we have a patient portal registration token, we can construct TB2 Patient Portal URL
            // Start the redirect countdown
            
            setIsRedirecting(true);
            
            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        handleContinue();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(countdownInterval);
        }
    }, [state, patientPortalURL]);

    useEffect(() => {
        const storedTelegraUrl = localStorage.getItem('tb2_telegra_url');
        
        if (storedTelegraUrl) {
            setTelegraUrl(storedTelegraUrl);
        }

        const storedPlan = localStorage.getItem('tb2_selected_plan');
        if (storedPlan) {
            try {
                setPlan(JSON.parse(storedPlan));
            } catch (e) {
                console.error('Error parsing stored plan:', e);
            }
        }
    }, []);

    // Add countdown and auto-redirect effect
    useEffect(() => {
        if (telegraUrl) {
            setIsRedirecting(true);
            
            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        handleContinue();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(countdownInterval);
        }
    }, [telegraUrl]);

    const handleContinue = async () => {
        // Determine destination: Telegra URL if available, otherwise intake
        const destination = state?.patientPortalRegistrationToken ? `${patientPortalURL}/signup?token=${state.patientPortalRegistrationToken}` : telegraUrl;
        
        try {
            const response = await fetch(process.env.REACT_APP_TB2_API_URL + '/events/redirect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    source: 'payment_success',
                    subdomain: localStorage.getItem('subdomain'),
                    redirectUrl: destination,
                    userType: telegraUrl ? 'telegra' : 'ola',
                    planInfo: plan ? { title: plan.title, total: plan.total } : null
                }),
            });

            const data = await response.json();
        } catch (error) {
            console.error('Error capturing redirect:', error);
        } finally {
            // Perform the redirect regardless of whether the event capture succeeded
            // Use replace() instead of href to avoid adding to browser history
            window.location.replace(destination);
        }
    };

    const handleContactSupport = () => {
        window.location.href = 'mailto:support@tb2.health';
    };

    return (
        <>
            <Header />
            
            <div className={styles.successContainer}>
                <div className={styles.successContent}>
                    <div className={styles.successMessage}>
                        <div className={styles.checkmarkContainer}>
                            <div className={styles.checkmark}>✓</div>
                        </div>
                        <h2 className={styles.successTitle}>Payment Confirmed!</h2>
                        <p className={styles.successSubtitle}>
                            Thank you for choosing TB2.health. Your subscription has been successfully activated.
                            {plan && (
                                <span className={styles.successPlanDetails}>
                                    <br />
                                    <strong>Your Program:</strong> {plan.title} - ${plan.total}
                                </span>
                            )}
                        </p>
                        
                        {/* Countdown message for auto-redirect */}
                        {isRedirecting && (telegraUrl || state?.patientPortalRegistrationToken) && (
                            <div className={styles.countdownMessage}>
                                <p>
                                    You will be automatically redirected to continue your journey in <strong>{countdown}</strong> seconds.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* What Happens Next */}
                    <div className={styles.nextSteps}>
                        <h3 className={styles.sectionTitle}>What Happens Next?</h3>
                        <div className={styles.stepsList}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepContent}>
                                    <h4>Complete Your Medical Assessment</h4>
                                    <p>
                                        {telegraUrl && "You'll be redirected to complete a comprehensive medical questionnaire that helps our licensed clinicians understand your health history and goals."}
                                        {state?.patientPortalRegistrationToken && (
                                            <span>
                                                You'll be redirected to your TB2 Patient Portal to complete a comprehensive medical questionnaire that helps our licensed clinicians understand your health history and goals,
                                                or you can click this link to access the patient portal directly: <a target='_blank' href={`${patientPortalURL}/signup?token=${state.patientPortalRegistrationToken}`} rel='noreferrer'>{patientPortalURL}</a>.
                                            </span>
                                        )}
                                        {!telegraUrl && !state?.patientPortalRegistrationToken && (
                                            <>
                                                <span className={styles.highlightedText}>You will be contacted shortly via email with a link to your secure medical assessment</span> that helps our licensed clinicians understand your health history and goals,
                                                or access the patient portal at <a target='_blank' href='https://patient.tb2.health' rel='noreferrer'>https://patient.tb2.health</a>.
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>
                            
                            {telegraUrl && (
                                <div className={styles.continueButtonContainer}>
                                    <button className={styles.continueButton} onClick={handleContinue}>Continue to Medical Assessment</button>
                                </div>
                            )}
                            
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepContent}>
                                    <h4>Clinician Review (24-48 hours)</h4>
                                    <p>A licensed healthcare provider will review your information to determine if you're a good candidate for the program and create your personalized treatment plan.</p>
                                </div>
                            </div>
                            
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepContent}>
                                    <h4>Get Your Prescription & Support</h4>
                                    <p>Once approved, your medication will be prescribed and shipped directly to you. You'll also be assigned a Patient Care Coordinator for ongoing support.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Important Information */}
                    <div className={styles.importantInfo}>
                        <h3 className={styles.sectionTitle}>Important Information</h3>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <h4>Money-Back Guarantee</h4>
                                <p>If our clinicians determine you're not eligible for the program, you'll receive a full refund.</p>
                            </div>
                            <div className={styles.infoItem}>
                                <h4>Ongoing Support</h4>
                                <p>Your Patient Care Coordinator will check in with you regularly and is available to answer questions.</p>
                            </div>
                            <div className={styles.infoItem}>
                                <h4>Cancel Anytime</h4>
                                <p>You can cancel your subscription at any time with no penalty. Contact your care coordinator for assistance.</p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className={styles.contactSection}>
                        <h3 className={styles.sectionTitle}>Questions?</h3>
                        <p>If you have any questions about your subscription or need assistance, our support team is here to help.</p>
                        <div className={styles.contactButtons}>
                            <button 
                                className={styles.supportButton}
                                onClick={handleContactSupport}
                            >
                                Contact Support
                            </button>
                            <div className={styles.supportEmail}>
                                support@tb2.health
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Footer />
        </>
    );
}

export default PaymentSuccess;
